import React, { forwardRef, useCallback, useState, useRef, useEffect } from "react";
import GenericMappingForm from "../GenericMappingForm";
import { FieldTypes, generateFormFields, parseSelectOptions } from "utils/formUtils";
import { Form, Button, message, Tooltip } from "antd";
import {
  isEmpty,
  map,
  some,
  split,
  isUndefined,
  uniq,
  forEach,
  isString,
  compact,
  includes,
  find,
  filter,
} from "utils/lodashUtils";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { useFeatureFlag } from "contexts/FeatureFlagContext";
import { isNull } from "lodash";

export const SCAN_TYPE_OPTIONS = [
  { label: "Infrastructure", value: "infrastructure" },
  { label: "Container Security - Kubernetes", value: "kubernetes",},
  {
    label: "Container Security - Images",
    value: "images",
  },
  { label: "CSPM", value: "cspm",},
];

export const CLOUD_PROVIDER_OPTIONS = ["AWS", "Azure"].map((i) => ({
  label: i,
  value: i.toLowerCase(),
}));

const initialFormValues = {
  product: undefined,
  subProduct: undefined,
  environment: undefined,
  scanType: "infrastructure",
  projectList: [{ clusterName: undefined, checknamespaces: true }],
  cspmList: [{ cloudProvider: undefined }],
  imageList: [{ registry: undefined }],
  imageTags:"",
  imageDigests:"",
  imageIds:"",
};

const CrowdStrikeFalconMappingForm = forwardRef(
  function CrowdStrikeFalconMappingForm(props, ref) {
    const [form] = Form.useForm();

    const [selectedType, setSelectedType] = useState("infrastructure");

    const [imageRepositories, setImageRepositories] = useState({});

    const [cloudAccounts, setCloudAccounts] = useState([]);

    const cloudAccountRef = useRef({});

    const imageRef = useRef({});

    const registryRef = useRef({type: "registry"});

    //For Infrastructure -> Host Groups
    const selectedNames = useRef({});

    const [clusters, setClusters] = useState([]);

    const [namespacesOptions, setNameSpacesOptions] = useState({});

    //For Infrastructure
    const [ingestionMode, setIngestionMode] = useState(() => {
      if(props.record) {
        return !isNull(props.record.tagValues) ? "tags" : "host_groups"
      }
    });

    useEffect(() => {
      if (props.record && !isEmpty(clusters)) {
        const { clusterWithNamespace } = props.record;

        const namespaceObj = {}
        forEach(clusterWithNamespace.split(","), (clusterandNamespace) => {
          const [clusterName] = clusterandNamespace.split("|:");
          const cluster = find(clusters, { id: clusterName });
          if (cluster) {
            namespaceObj[clusterName] = cluster?.otherProperties?.namespaces || []
          }
        });
        setNameSpacesOptions({...namespaceObj})
      }
    }, [props.record, clusters]);

    const featureFlags = useFeatureFlag();

    const [initialValues] = useState(() => {
      if (props.record) {
        return [];
      }
      return [{ ...initialFormValues }];
    });

    const fetchProjects = useCallback(
      (params) => {
        const { promise, cancel } =
          ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, {
            login_id: props.loginConfigId,
            ...params,
          });
        return { promise: promise.then((response) => {
          const { projects } = response;
          if(params.type === "repository"){
            setImageRepositories((prev) => ({...prev, [params.project]: projects}))
          }
          if(params.type === "cloudProvider"){
            setCloudAccounts((prev) => [...prev, ...projects])
          }
          if(params.type === "clusters"){
            setClusters(projects)
          }
          return projects;
          }), cancel };
      },
      [props.toolName, props.loginConfigId]
    );

    const fetchClusters = useCallback(() => fetchProjects({type: "clusters"}), [fetchProjects]);


    const fetchRepos = useCallback(
      (params) => {
        const { promise, cancel } =
          ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, {
            login_id: props.loginConfigId,
            type: "repositories",
            ...params,
          });
        return { promise: promise.then((response) => {
          const { projects } = response;
          return projects;
        }), cancel };
      },
      [props.toolName, props.loginConfigId],
    );

    const onScanTypeChange = useCallback((e) => {
      setSelectedType(e.target.value);
    }, []);

    const fetchHostGroups = useCallback((params) => fetchProjects({ ...params, type: "host_groups"}), [fetchProjects]);

    const projectListRender = useCallback(
      (listField) => {

        const onIngestionModeChange = (value, field) => {
          setIngestionMode(value);
          const { name } = field;
          const configurations = form.getFieldValue("configurations") || [];
          const updatedConfiguration = [...configurations];
          updatedConfiguration[name] = {
            ...updatedConfiguration[name],
            mode: value,
            hostGroups: undefined,
            tagValues: undefined,
          };
    
          form.setFieldsValue({ configurations: updatedConfiguration });
        };

        const getInfrastructureFields = () => {
          const fields = [
            {
              name: "mode",
              type: FieldTypes.SELECT,
              colSpan: 12,
              label: "Select Ingestion Mode",
              options: [{label: "Tags", value: "tags"}, {label: "Host Groups", value: "host_groups"}],
              props: {
                onChange: onIngestionModeChange
              },
              required: true
            },
            ...(ingestionMode === "host_groups" ? [{
              name: "hostGroups",
              label: "Host Groups",
              type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
              props: {
                mode: "multiple",
                onChange: (value, field, selectedOptions) => {
                  //handling multiple mapping case using index
                  selectedNames.current[field.name] = map(selectedOptions, "name");
                },
                api: fetchHostGroups,
              }
            }] : ingestionMode === "tags" ? [{
              name: "tagValues",
              colSpan: "12",
              type: FieldTypes.TAGS,
              label: "Tag Values",
              notFoundContent: null,
            }] : [])
          ];
          return generateFormFields(fields, 10, listField);
        };

        const getKubernetesFields = () => {

          const onClusterChange = 
            (cluster, field) => {
              const { name } = field;
              const configurations = form.getFieldValue("configurations");
              const currentCluster =
                configurations[listField.name]?.projectList[name]?.clusterName;
              setNameSpacesOptions((namespaces) => ({
                ...namespaces,
                [currentCluster]: find(clusters, {id: currentCluster})
                  ?.otherProperties?.namespaces,
            }));
          };

          const kubernetesFields = [
            {
              name: "clusterName",
              label: "Clusters",
              colSpan: 7,
              type: FieldTypes.SELECT,
              required: true,
              props: {
                api: fetchClusters,
                onChange: onClusterChange,
                optionsMutator: (options) => {
                  const allConfigs = form.getFieldsValue().configurations;

                  const selectedClusterList = map(
                    allConfigs[listField.name]?.projectList,
                    (list) => list.clusterName
                  );

                  return map(options, (opt) => ({
                    ...opt,
                    label: opt.id === opt.name ? opt.name : `${opt.name} - ${opt.id}`,
                    disabled: selectedClusterList.includes(opt.value),
                  }));
                },
                disableScrollPagination: true,
                minSearchLength: 3,
              },
            },
            {
              name: "namespaces",
              label: <span className="required-mark">NameSpaces</span>,
              placeholder: "Namespaces",
              colSpan: 7,
              type: FieldTypes.SELECT,
              props: {
                mode: "multiple",
                getFormListOptions: (nestedListFieldName) => {
                  const allConfigs = form.getFieldsValue().configurations;

                  const clusterName =
                    allConfigs[listField.name]?.projectList[
                      nestedListFieldName
                    ]?.clusterName;
                  
                  return parseSelectOptions(namespacesOptions[clusterName]);
                },
                setExtraProps: (field, nestedListField) => {
                  const allConfigs = form.getFieldsValue().configurations;
                  const namespacesChecked =
                      allConfigs[listField.name]?.projectList[
                        nestedListField.name
                      ]?.checknamespaces;
          
                  return {
                    allowClear: true,
                    disabled: namespacesChecked,
                  };
                },
            },
            },
            {
              name: "checknamespaces",
              label: "Select All Namespaces",
              colSpan: 7,
              type: FieldTypes.BOOLEAN,
              inline: true,
              className: "m-t-l",
            },
            {
              name: "add",
              colSpan: 2,
              render: ({ remove, name, fields }) => {
                return (
                  <Button
                    disabled={fields.length === 1}
                    type="dashed"
                    onClick={() => {
                      remove(name);
                    }}
                    className="m-t-l"
                  >
                    -
                  </Button>
                );
              },
            },
          ];
          return (
            <Form.List name={[listField.name, "projectList"]}>
              {(fields, { add, remove }) => {
                return (
                  <div>
                    {map(fields, (field) => {
                      return generateFormFields(
                        kubernetesFields,
                        10,
                        { ...field, add, remove, fields },
                        field.name
                      );
                    })}
                    <Button
                      size="small"
                      type="dashed"
                      onClick={() =>
                        add({ clusterName: undefined, checknamespaces: true })
                      }
                    >
                      + Add Cluster Name
                    </Button>
                  </div>
                );
              }}
            </Form.List>
          );
        };

        const getCspmFields = () => {

          const finalProjectOptions = (projects, listField) => {
            const selected = compact(
              map(
                form.getFieldsValue().configurations?.[listField.name]?.cspmList,
                (p) => p?.cloudProvider
              )
            );
            return map(projects, (p) => ({
              ...p,
              disabled: includes(selected, p.value),
            }));
          };

          const cspmListRender = (listField) => {
            const __fields = [
              {
                name: "cloudProvider",
                label: "Cloud Provider",
                colSpan: 10,
                type: FieldTypes.SELECT,
                options: CLOUD_PROVIDER_OPTIONS,
                props: {
                  setExtraProps: () => {
                    const allConfigs = form.getFieldsValue().configurations;
                    const selectedCloudGroups =
                      allConfigs[listField.name]?.cloudGroups;
                    return {
                      disabled: selectedCloudGroups,
                      optionsMutator: (options) => finalProjectOptions(options, listField)
                    };
                  },
                  allowClear: true,
                },
              },
              {
                name: "cloudProviderAccounts",
                label: "Cloud Account",
                colSpan: 10,
                type: FieldTypes.MULTI_SELECT,
                props: {
                  setExtraProps: (field, nestedListField) => {
                    const allConfigs = form.getFieldsValue().configurations;
                    const selectedCloudGroups =
                      allConfigs[listField.name]?.cloudGroups;
                    const cloudProvider =
                      allConfigs[listField.name]?.cspmList?.[
                        nestedListField.name
                      ]?.cloudProvider;

                    const uniqueKey = `${listField.name}${nestedListField.name}`;

                    if (
                      cloudProvider &&
                      (!cloudAccountRef.current[uniqueKey] ||
                        cloudProvider !==
                          cloudAccountRef.current[uniqueKey]?.project)
                    ) {
                      cloudAccountRef.current[uniqueKey] = {
                        project: cloudProvider,
                        type: "cloudProvider",
                      };
                    }
                    return {
                      api: cloudProvider && fetchProjects,
                      apiParams: cloudAccountRef.current[uniqueKey],
                      resetOptionsProp: cloudProvider,
                      disabled: selectedCloudGroups,
                      allowClear: true,
                      // optionsMutator,
                    };
                  },
                },
              },
              {
                name: "add",
                colSpan: 2,
                render: ({ remove, name, fields }) => {
                  return (
                    <Button
                      disabled={fields.length === 1}
                      type="dashed"
                      onClick={() => {
                        remove(name);
                      }}
                      className="m-t-l"
                    >
                      -
                    </Button>
                  );
                },
              },
            ];
            const selectedCloudProviders = uniq(compact(
              map(
                form.getFieldsValue().configurations?.[listField.name]?.cspmList,
                (p) => p?.cloudProvider
              )
            ));

            const disabledAddButton = map(CLOUD_PROVIDER_OPTIONS, option => option.value).every(value => selectedCloudProviders.includes(value))
            return (
              <div className="m-b-sm">
                <Form.List name={[listField.name, "cspmList"]}>
                  {(fields, { add, remove }) => {
                    // Disable the "Add" button when no Cloud Provider or Account is selected,
                    // since all data is already fetched by default when none are selected.
                    return (
                      <div>
                        {map(fields, (field) => {
                          return generateFormFields(
                            __fields,
                            10,
                            { ...field, add, remove, fields },
                            field.name
                          );
                        })}
                        <Button
                          size="small"
                          type="dashed"
                          disabled={disabledAddButton}
                          onClick={() => add({ cloudProvider: undefined })}
                        >
                          + Add Cloud Provider
                        </Button>
                      </div>
                    );
                  }}
                </Form.List>
              </div>
            );
          };

          const cspmFields = [
            // {
            //   name: "cloudGroups",
            //   label: "Cloud Group",
            //   colSpan: 14,
            //   type: FieldTypes.SELECT,
            //   options: [],
            //   props: {
            //     setExtraProps: (field, nestedListField) => {
            //       const allConfigs = form.getFieldsValue().configurations;
            //       const selectedCloudProvider =
            //         allConfigs[listField.name]?.cspmList?.[nestedListField.name]
            //           ?.cloudProvider;
            //       return {
            //         disabled: selectedCloudProvider,
            //         allowClear: true,
            //       };
            //     },
            //   },
            // },
            {
              name: (listField) => [listField.name, "cspmList"],
              render: cspmListRender,
            },
          ];
          return generateFormFields(cspmFields, 24, listField);
        };

        const getImagesFields = () => {
          const getRegistryOptions = (projects, listField) => {
            const selected = compact(
              map(
                form.getFieldsValue().configurations?.[listField.name]
                  ?.imageList,
                (p) => p?.registry
              )
            );
            return map(projects, (registry) => {
              const hasNoImageRepos = isEmpty(registry.otherProperties?.repositories);

              return ({
              label: 
              <Tooltip title={hasNoImageRepos ? "This registry does not have any repositories to fetch vulnerabilities from. Please select a different registry" : ""}>
                {registry.name}
              </Tooltip>,
              value: registry.name,
              disabled: includes(selected, registry.name) || isEmpty(registry.otherProperties?.repositories),
            })});
          };

          const imageListRender = (listField) => {
            const __fields = [
              {
                name: "registry",
                colSpan: "10",
                type: FieldTypes.SELECT,
                label: "Registry",
                required: true,
                props: {
                  api: fetchProjects,
                  apiParams: registryRef.current,
                  optionsMutator: (options) =>
                    getRegistryOptions(options, listField),
                  optionFilterProp: "value"
                },
              },
              {
                name: "repository",
                colSpan: "10",
                type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
                label: "Image Repositories",
                props: {
                  mode: "multiple",
                  disableScrollPagination: true,
                  minSearchLength: 3,
                  setExtraProps: (field, nestedListField) => {
                    const allConfigs = form.getFieldsValue().configurations;
                    const registry =
                      allConfigs[listField.name]?.imageList?.[
                        nestedListField.name
                      ]?.registry;

                    const uniqueKey = `${listField.name}${nestedListField.name}`;

                    if (
                      registry &&
                      (!imageRef.current[uniqueKey] ||
                        registry !== imageRef.current[uniqueKey]?.project)
                    ) {
                      imageRef.current[uniqueKey] = {
                        project: registry,
                        type: "repository",
                      };
                    }
                    return {
                      api: registry && fetchProjects,
                      apiParams: imageRef.current[uniqueKey],
                      resetOptionsProp: registry,
                      allowClear: true,
                    };
                  },
                },
              },
              {
                name: "add",
                colSpan: 2,
                render: ({ remove, name, fields }) => {
                  return (
                    <Button
                      disabled={fields.length === 1}
                      type="dashed"
                      onClick={() => {
                        remove(name);
                      }}
                      className="m-t-l"
                    >
                      -
                    </Button>
                  );
                },
              },
            ];

            return (
              <div className="m-b-sm">
                <Form.List name={[listField.name, "imageList"]}>
                  {(fields, { add, remove }) => {
                    return (
                      <div>
                        {map(fields, (field) => {
                          return generateFormFields(
                            __fields,
                            10,
                            { ...field, add, remove, fields },
                            field.name
                          );
                        })}
                        <Button
                          size="small"
                          type="dashed"
                          onClick={() => add({ registry: undefined })}
                        >
                          + Add Registry
                        </Button>
                      </div>
                    );
                  }}
                </Form.List>
              </div>
            );
          };
          const imageFields = [
            {
              name: (listField) => [listField.name, "imageList"],
              render: imageListRender,
            },
          ];
          return generateFormFields(imageFields, 24, listField);
        };

        const getImagesWithoutRegistryFields = () => {
            const imageFields = [
             
              {
                name: "repository",
                colSpan: "10",
                type: FieldTypes.SELECT,
                label: "Image Repositories",
                props: {
                  api: fetchRepos,
                     
                },
              },
            ];
         
          return generateFormFields(imageFields, 24, listField);
        }

        const SCAN_TYPE_MAP = {
          infrastructure: getInfrastructureFields(),
          kubernetes: getKubernetesFields(),
          cspm: getCspmFields(),
          images: featureFlags.ENABLE_CONTAINER_DETAIL_WITHOUT_REGISTRY_CROWDSTRIKE ? getImagesWithoutRegistryFields() : getImagesFields()
        };

        const selectedscanType =
          form.getFieldsValue().configurations[listField.name]?.scanType;
        return <div className="m-b-sm">{SCAN_TYPE_MAP[selectedscanType]}</div>;
      },
      [
        featureFlags.ENABLE_CONTAINER_DETAIL_WITHOUT_REGISTRY_CROWDSTRIKE,
        form,
        clusters,
        fetchClusters,
        namespacesOptions,
        fetchProjects,
        fetchRepos,
        ingestionMode,
      ],
    );

    const extraFields = [
      {
        name: "scanType",
        type: FieldTypes.RADIO,
        options: map(SCAN_TYPE_OPTIONS, (scanType) => ({
          ...scanType,
          disabled: scanType.flagDisabled
            ? !featureFlags[scanType.flagDisabled]
            : scanType.disabled,
          label: (
            <Tooltip title={scanType.disabled ? "Coming Soon" : ""}>
              {scanType.label}
            </Tooltip>
          ),
        })),
        props: {
          onChange: onScanTypeChange,
          defaultValue: selectedType,
        },
      },
      {
        name: (listField) => [listField.name, "projectList"],
        render: projectListRender,
      },
    ];

    const getExtraFieldsData = useCallback((config, index) => {
      const { scanType } = config;
      if (!scanType) {
        message.error("Please select atleast one Scan Type Radio Option");
        throw new Error("Please select atleast one Scan Type Radio Option");
      }
      const extraFieldData = {};
      extraFieldData.type = scanType;

      //handling multiple mapping case using index
      //to prevent wrong name map when multiple configurations are saved
      const configSelectedNames = selectedNames.current[index];

      switch (scanType) {
        case "infrastructure": {
          const { tagValues, hostGroups, mode } = config;
          if(mode === "tags") {
            extraFieldData.tagValues =  !tagValues ? "" : tagValues.join();
            break;
          } 
          else {
            extraFieldData.hostGroups = !hostGroups ? "" : hostGroups.join();
            extraFieldData.hostGroupNames = !hostGroups ? "" : configSelectedNames.join();
            break;
          }
        }
        case "images": {

        if(featureFlags.ENABLE_CONTAINER_DETAIL_WITHOUT_REGISTRY_CROWDSTRIKE){
          const { repository } = config
          extraFieldData.repository = repository;
          break;
        }
          const { imageList } = config;
          let registry = [];

          extraFieldData.repository = map(imageList, (object) => {
            if (!isEmpty(object.repository)) {
              const repoNames = map(object.repository, (repository) => {
                const imageRepository = imageRepositories[object.registry].find(
                  (el) => repository && repository.includes(el.id)
                );
                return imageRepository ? imageRepository.name : "";
              });
              return repoNames.join();
            } else {
              return "All";
            }
          }).join(";");

          // Collect all registries
          registry = map(imageList, "registry");
          extraFieldData.registry = registry.join();

          break;
        }
        case "kubernetes": {
          const { projectList } = config;
          const clusterNamesArr = []
          extraFieldData.clusterWithNamespace = map(projectList, (object) => {
            if (isEmpty(object.namespaces) && !object.checknamespaces) {
              message.error(
                "Please select atleast one Namespace or check all Namespaces"
              );
              throw new Error(
                "Please select atleast one Namespace or check all Namespaces"
              );
            }
            let namespaces;
            if (object.checknamespaces) {
              namespaces = `|:all`;
            } else {
              namespaces = !isEmpty(object.namespaces)
                ? `|:${object.namespaces.join(";")}`
                : "";
            }
            const clustersName = find(clusters, { id: object.clusterName})?.name;
            if(clustersName){
              clusterNamesArr.push(clustersName)
            }
            return `${object.clusterName}${namespaces}`;
          }).join();
            extraFieldData.clusterName = clusterNamesArr.join()
          break;
        }
        case "cspm": {
          const { cspmList } = config;
          extraFieldData.type = "cloudProvider";
          // Send "All" even if the user selects specific providers/accounts in other rows,
          // since an empty Cloud Provider and Account indicates a request for all data.
          if (
            (isUndefined(cspmList) ||
              some(
                cspmList,
                (list) =>
                  isUndefined(list.cloudProvider)
              ) ||
              isEmpty(cspmList))
          ) {
            extraFieldData.cloudProviderWithAccounts = "All";
            extraFieldData.cloudProviderWithAccountsName = "All";
            extraFieldData.cloudProvider = "";
            break;
          }
  
          const cloudProviders = [];
          extraFieldData.cloudProviderWithAccounts = map(
            cspmList,
            (object) => {
              const { cloudProviderAccounts, cloudProvider } = object
              const cloudAccounts = `|:${!isEmpty(cloudProviderAccounts) ? cloudProviderAccounts.join(";") : "All"}`
              cloudProviders.push(cloudProvider);
              return `${cloudProvider}${cloudAccounts}`;
            }
          ).join();
          extraFieldData.cloudProviderWithAccountsName = map(
            cspmList,
            (object) => {
              const cloudAccountNames = map(object.cloudProviderAccounts, (account) => find(cloudAccounts, { id: account })?.name ?? account);
              const cloudAccountNamesString = `|:${
                !isEmpty(cloudAccountNames)
                  ? cloudAccountNames.join(";")
                  : "All"
              }`;
              const cloudProviderName = find(CLOUD_PROVIDER_OPTIONS, { value: object.cloudProvider })?.label;
              return `${cloudProviderName}${cloudAccountNamesString}`;
            }
          ).join();
          extraFieldData.cloudProvider = uniq(cloudProviders).join();
        
          break;
        }
        default:
          break;
      }
      return extraFieldData;
    }, 
    [
      featureFlags.ENABLE_CONTAINER_DETAIL_WITHOUT_REGISTRY_CROWDSTRIKE,
      imageRepositories,
      clusters,
      cloudAccounts,
    ],);

    const parseExtraFieldsDataFromResponse = useCallback((config) => {
      const { type } = config;
      const parsedFields = {};
      const finalType =
        ["cloudGroups", "cloudProvider"].indexOf(type) !== -1 ? "cspm" : type;
      switch (finalType) {
        case "infrastructure": {
          const { tagValues, hostGroups, hostGroupNames } = config;
          parsedFields.tagValues = tagValues ? split(tagValues, ",") : [];
          //keeping the index as 0 as we show only one configuration on edit
          //as the configurations gets saved individually for multiple mappings too
          selectedNames.current[0] = hostGroupNames
            ? split(hostGroupNames, ",")
            : [];
          parsedFields.hostGroups = hostGroups ? split(hostGroups, ",") : [];
          parsedFields.mode = !isNull(tagValues) ? "tags" : "host_groups";

          break;
        }
        case "kubernetes": {
          const { clusterWithNamespace } = config;
          parsedFields.projectList = map(
            clusterWithNamespace.split(","),
            (clusterandNamespace) => {
              const [clusterName, ...namespaces] =
                clusterandNamespace.split("|:");
              return {
                clusterName,
                namespaces:
                  isEmpty(namespaces) || namespaces[0] === "all"
                    ? []
                    : split(namespaces, ";"),
                checknamespaces: namespaces[0] === "all",
              };
            }
          );
          break;
        }
        case "cspm": {
          const { cloudProviderWithAccounts } = config;
          // parsedFields.cloudGroups = cloudGroups;
          parsedFields.cspmList = map(
            cloudProviderWithAccounts.split(","),
            (providerAndAccounts) => {
              const [cloudProvider, ...accounts] =
                providerAndAccounts.split("|:");
              return {
                cloudProvider: cloudProvider === "All" ? undefined : cloudProvider,
                cloudProviderAccounts: cloudProvider === "All" || accounts[0] === "All" ? undefined : split(accounts, ";"),
              };
            }
          );
          break;
        }
          case "images": {
            const { repository, registry } = config;
            if(featureFlags.ENABLE_CONTAINER_DETAIL_WITHOUT_REGISTRY_CROWDSTRIKE){
              parsedFields.repository = repository
              break;
            }
            parsedFields.imageList = map(registry.split(","), (reg, index) => {
              const repoArray = split(split(repository, ";")[index], ",");
              return {
                repository: filter(repoArray, (repo) => repo !== "All"),
                registry: reg,
              };
            });
            break;
          }
        default:
          break;
      }
      setSelectedType(finalType);
      parsedFields.scanType = finalType;
      return parsedFields;
    }, [featureFlags.ENABLE_CONTAINER_DETAIL_WITHOUT_REGISTRY_CROWDSTRIKE]);

    const onValuesChange = useCallback(
      (values) => {
        if (values.configurations) {
          let isProjectChanged = false;
          const formValues = form.getFieldsValue();
          forEach(values.configurations, (configs, index) => {
            //look into SnykMappingFormV2 for explanation
            if (
              !configs ||
              (!configs.projectList && !configs.cspmList && !configs.imageList) ||
              (isString(configs.projectList) && isString(configs.cspmList) && isString(configs.imageList))
            ) {
              return;
            }

            forEach(configs.projectList, (object, i) => {
              //look into SnykMappingFormV2 for explanation
              if (
                object &&
                object.clusterName &&
                Object.keys(object).length === 1
              ) {
                isProjectChanged = true;
                formValues.configurations[index].projectList[i].namespaces =
                  undefined;
              }
            });
            forEach(configs.cspmList, (object, i) => {
              //look into SnykMappingFormV2 for explanation
              //handle case when the cloud Provider field is cleared
              if (object && 'cloudProvider' in object) {
                if (!object.cloudProvider || Object.keys(object).length === 1) {
                  isProjectChanged = true;
                  formValues.configurations[index].cspmList[i].cloudProviderAccounts = undefined;
                }
              }
            });

            forEach(configs.imageList, (object, i) => {
              if (
                object &&
                object.registry &&
                Object.keys(object).length === 1
              ) {
                isProjectChanged = true;
                formValues.configurations[index].imageList[
                  i
                ].repository = undefined;
              }
            });
          
          });
          if (isProjectChanged) {
            form.setFieldsValue(formValues);
          }
        }
      },
      [form]
    );

    return (
      <GenericMappingForm
        ref={ref}
        form={form}
        getExtraFieldsData={getExtraFieldsData}
        parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
        extraFields={extraFields}
        formDefaultValues={initialValues}
        newRowDefaultValue={initialValues[0]}
        onValuesChange={onValuesChange}
        {...props}
      />
    );
  }
);

export default CrowdStrikeFalconMappingForm;
